'use client';

import { useSyncExternalStore } from 'react';
import type { PlinthMaterialId } from '../theming/plinths/types';

/** Default pedestal for every seat until the user picks a material in Profile. */
export const DEFAULT_PLAYER_PLINTH: PlinthMaterialId = 'stone';

const STORAGE_KEY = 'takeover.playerPlinth.v1';

const VALID_IDS: readonly PlinthMaterialId[] = [
  'stone',
  'obsidian',
  'marble',
  'sandstone',
  'slate',
  'wood',
];

function isValid(v: string): v is PlinthMaterialId {
  return (VALID_IDS as readonly string[]).includes(v);
}

let cache: PlinthMaterialId = DEFAULT_PLAYER_PLINTH;
const listeners = new Set<() => void>();

function readCacheFromStorage(): PlinthMaterialId {
  if (typeof window === 'undefined') return DEFAULT_PLAYER_PLINTH;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isValid(raw)) return raw;
  } catch {
    // ignore
  }
  return DEFAULT_PLAYER_PLINTH;
}

function syncCache(): void {
  cache = readCacheFromStorage();
}

if (typeof window !== 'undefined') {
  syncCache();
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    syncCache();
    listeners.forEach((l) => l());
  });
}

export function subscribePlayerPlinth(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function getPlayerPlinthSnapshot(): PlinthMaterialId {
  return cache;
}

export function getServerPlayerPlinthSnapshot(): PlinthMaterialId {
  return DEFAULT_PLAYER_PLINTH;
}

/**
 * Persist the viewer's pedestal choice and refresh all subscribers (2D/3D board).
 */
export function setPlayerPlinth(id: PlinthMaterialId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore quota / private mode
  }
  cache = id;
  listeners.forEach((l) => l());
}

/** Live preference for the current user's own-seat plinth (default stone). */
export function usePlayerPlinthMaterial(): PlinthMaterialId {
  return useSyncExternalStore(
    subscribePlayerPlinth,
    getPlayerPlinthSnapshot,
    getServerPlayerPlinthSnapshot,
  );
}
